import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Slider, IconButton, Tooltip, Paper,
  FormControl, Select, MenuItem, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import MapIcon from '@mui/icons-material/Map';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getAvailableYears, getGeoJsonByYear } from '../services/api';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { basemapFor } from '../theme';

// State colors for electorates (used for state-based coloring mode)
const STATE_COLORS = {
  'New South Wales': { color: '#1565c0', fill: '#42a5f5' },
  'Victoria': { color: '#7b1fa2', fill: '#ba68c8' },
  'Queensland': { color: '#c62828', fill: '#ef5350' },
  'Western Australia': { color: '#f57c00', fill: '#ffb74d' },
  'South Australia': { color: '#2e7d32', fill: '#66bb6a' },
  'Tasmania': { color: '#00838f', fill: '#4dd0e1' },
  'Northern Territory': { color: '#6d4c41', fill: '#a1887f' },
  'Australian Capital Territory': { color: '#5e35b1', fill: '#9575cd' },
};

// Graph-coloring palette - 6 distinct colors that work well together
// These colors ensure adjacent electorates are visually distinguishable
const GRAPH_COLORS = [
  { color: '#1565c0', fill: '#64b5f6' },  // Blue
  { color: '#c62828', fill: '#ef5350' },  // Red
  { color: '#2e7d32', fill: '#81c784' },  // Green
  { color: '#f57c00', fill: '#ffb74d' },  // Orange
  { color: '#7b1fa2', fill: '#ba68c8' },  // Purple
  { color: '#00838f', fill: '#4dd0e1' },  // Teal
];

// Component to fit map to Australia bounds
const FitAustralia = () => {
  const map = useMap();
  useEffect(() => {
    // Australia bounds
    map.fitBounds([[-44, 112], [-10, 154]], { padding: [20, 20] });
  }, [map]);
  return null;
};

export default function ElectorateMapFullscreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const basemap = basemapFor(mode);

  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingGeoJson, setLoadingGeoJson] = useState(false);
  const [hoveredElectorate, setHoveredElectorate] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedState, setSelectedState] = useState('all');

  // Load available years on mount
  useEffect(() => {
    const loadYears = async () => {
      try {
        const data = await getAvailableYears();
        setAvailableYears(data.years || []);

        // Get year from URL or default to current year
        const urlYear = parseInt(searchParams.get('year'));
        if (urlYear && data.years.includes(urlYear)) {
          setSelectedYear(urlYear);
        } else {
          // Default to most recent year
          const currentYear = new Date().getFullYear();
          const defaultYear = data.years.includes(currentYear)
            ? currentYear
            : data.years[data.years.length - 1];
          setSelectedYear(defaultYear);
        }
      } catch (err) {
        console.error('Error loading years:', err);
      }
      setLoading(false);
    };
    loadYears();
  }, [searchParams]);

  // Load GeoJSON when year changes
  useEffect(() => {
    if (!selectedYear) return;

    const loadGeoJson = async () => {
      setLoadingGeoJson(true);

      try {
        // Update URL
        setSearchParams({ year: selectedYear.toString() });

        // Load bulk GeoJSON from server
        const data = await getGeoJsonByYear(selectedYear);
        setGeoJsonData(data);
      } catch (err) {
        console.error('Error loading GeoJSON:', err);
        setGeoJsonData(null);
      }
      setLoadingGeoJson(false);
    };

    loadGeoJson();
  }, [selectedYear, setSearchParams]);

  // Animation playback
  useEffect(() => {
    if (!isPlaying || availableYears.length === 0) return;

    const interval = setInterval(() => {
      setSelectedYear(prev => {
        const currentIdx = availableYears.indexOf(prev);
        if (currentIdx < availableYears.length - 1) {
          return availableYears[currentIdx + 1];
        } else {
          setIsPlaying(false);
          return prev;
        }
      });
    }, 2000); // 2 seconds per year for faster animation

    return () => clearInterval(interval);
  }, [isPlaying, availableYears]);

  const handleYearChange = (event, newValue) => {
    setSelectedYear(availableYears[newValue]);
  };

  const handlePrevYear = () => {
    const idx = availableYears.indexOf(selectedYear);
    if (idx > 0) {
      setSelectedYear(availableYears[idx - 1]);
    }
  };

  const handleNextYear = () => {
    const idx = availableYears.indexOf(selectedYear);
    if (idx < availableYears.length - 1) {
      setSelectedYear(availableYears[idx + 1]);
    }
  };

  // Filter features by state
  const filteredGeoJson = useMemo(() => {
    if (!geoJsonData || selectedState === 'all') return geoJsonData;
    return {
      ...geoJsonData,
      features: geoJsonData.features.filter(f => f.properties.state === selectedState)
    };
  }, [geoJsonData, selectedState]);

  const uniqueStates = useMemo(() => {
    if (!geoJsonData) return [];
    return [...new Set(geoJsonData.features.map(f => f.properties.state))].sort();
  }, [geoJsonData]);

  const getStyleForFeature = useCallback((feature) => {
    // Use graph coloring (colorIndex) for better visibility of boundaries
    // Falls back to state colors if colorIndex is not available
    const colorIndex = feature.properties.colorIndex;
    const colors = colorIndex !== undefined
      ? GRAPH_COLORS[colorIndex % GRAPH_COLORS.length]
      : STATE_COLORS[feature.properties.state] || { color: '#757575', fill: '#bdbdbd' };
    const isHovered = hoveredElectorate === feature.properties.id;
    // On the dark basemap a black hover outline disappears, so highlight with
    // white in dark mode (#159).
    const hoverColor = mode === 'dark' ? '#fff' : '#000';
    return {
      color: isHovered ? hoverColor : colors.color,
      weight: isHovered ? 3 : 1,
      fillColor: colors.fill,
      fillOpacity: isHovered ? 0.6 : 0.4
    };
  }, [hoveredElectorate, mode]);

  const onEachFeature = useCallback((feature, layer) => {
    const { electorate, state, eid, id } = feature.properties;

    layer.on({
      mouseover: () => setHoveredElectorate(id),
      mouseout: () => setHoveredElectorate(null),
      click: () => navigate(`/electorates/${eid}`)
    });

    layer.bindTooltip(`${electorate} (${state})`, {
      permanent: false,
      direction: 'center',
      className: 'electorate-tooltip'
    });
  }, [navigate]);

  if (loading) {
    return (
      <Box sx={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  const yearIndex = availableYears.indexOf(selectedYear);
  const totalElectorates = filteredGeoJson?.features?.length || 0;

  return (
    <Box sx={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#1a1a2e'
    }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1,
          borderRadius: 0,
          bgcolor: '#16213e',
          color: 'white',
          zIndex: 1000
        }}
      >
        <MapIcon />
        <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
          Australian Electoral Boundaries
        </Typography>

        <Typography variant="h4" sx={{ fontWeight: 700, color: '#4cc9f0', minWidth: 100, textAlign: 'center' }}>
          {selectedYear}
        </Typography>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            sx={{
              color: 'white',
              '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
              '.MuiSvgIcon-root': { color: 'white' }
            }}
          >
            <MenuItem value="all">All States</MenuItem>
            {uniqueStates.map(state => (
              <MenuItem key={state} value={state}>{state}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Chip
          label={loadingGeoJson ? 'Loading...' : `${totalElectorates} electorates`}
          size="small"
          sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}
        />

        <Tooltip title="Close">
          <IconButton
            component={RouterLink}
            to="/electorates"
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Loading indicator */}
      {loadingGeoJson && (
        <Box sx={{
          position: 'absolute',
          top: 56,
          left: 0,
          right: 0,
          zIndex: 1001,
          height: 4,
          bgcolor: 'rgba(255,255,255,0.1)'
        }}>
          <Box sx={{
            height: '100%',
            width: '30%',
            bgcolor: '#4cc9f0',
            animation: 'loading 1s infinite',
            '@keyframes loading': {
              '0%': { marginLeft: '0%' },
              '50%': { marginLeft: '70%' },
              '100%': { marginLeft: '0%' }
            }
          }} />
        </Box>
      )}

      {/* Map */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        <MapContainer
          center={[-25.0, 134.0]}
          zoom={4}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
        >
          <TileLayer
            key={basemap.url}
            attribution={basemap.attribution}
            url={basemap.url}
            subdomains={basemap.subdomains || 'abc'}
          />
          <FitAustralia />

          {filteredGeoJson && filteredGeoJson.features && (
            <GeoJSON
              key={`${selectedYear}-${selectedState}`}
              data={filteredGeoJson}
              style={getStyleForFeature}
              onEachFeature={onEachFeature}
            />
          )}
        </MapContainer>

        {/* State Legend */}
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            bottom: 100,
            right: 16,
            p: 1.5,
            bgcolor: 'rgba(255,255,255,0.95)',
            borderRadius: 2,
            zIndex: 1000,
            maxWidth: 200
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
            Electorate Boundaries
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 1 }}>
            Colors distinguish neighboring electorates
          </Typography>
          {GRAPH_COLORS.map((colors, idx) => (
            <Box key={idx} sx={{ display: 'inline-block', mr: 0.5, mb: 0.5 }}>
              <Box sx={{
                width: 16,
                height: 16,
                bgcolor: colors.fill,
                border: `2px solid ${colors.color}`,
                borderRadius: 0.5
              }} />
            </Box>
          ))}
        </Paper>

        {/* Hovered electorate info */}
        {hoveredElectorate && geoJsonData && (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              p: 2,
              bgcolor: 'rgba(255,255,255,0.95)',
              borderRadius: 2,
              zIndex: 1000
            }}
          >
            {(() => {
              const feature = geoJsonData.features.find(f => f.properties.id === hoveredElectorate);
              if (!feature) return null;
              return (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {feature.properties.electorate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.properties.state}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click to view details
                  </Typography>
                </>
              );
            })()}
          </Paper>
        )}
      </Box>

      {/* Timeline Controls */}
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          borderRadius: 0,
          bgcolor: '#16213e',
          color: 'white'
        }}
      >
        <Tooltip title="Previous Year">
          <span>
            <IconButton
              onClick={handlePrevYear}
              disabled={yearIndex <= 0 || loadingGeoJson}
              sx={{ color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
            >
              <SkipPreviousIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={isPlaying ? "Pause" : "Play Animation"}>
          <span>
            <IconButton
              onClick={() => setIsPlaying(!isPlaying)}
              disabled={loadingGeoJson}
              sx={{ color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Next Year">
          <span>
            <IconButton
              onClick={handleNextYear}
              disabled={yearIndex >= availableYears.length - 1 || loadingGeoJson}
              sx={{ color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
            >
              <SkipNextIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ flex: 1, mx: 2 }}>
          <Slider
            value={yearIndex >= 0 ? yearIndex : 0}
            min={0}
            max={availableYears.length - 1}
            onChange={handleYearChange}
            disabled={loadingGeoJson}
            marks={availableYears.filter((_, i) => i % 5 === 0 || i === availableYears.length - 1).map(year => ({
              value: availableYears.indexOf(year),
              label: year.toString()
            }))}
            sx={{
              color: '#4cc9f0',
              '& .MuiSlider-markLabel': { color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' },
              '& .MuiSlider-thumb': { bgcolor: '#4cc9f0' },
              '& .MuiSlider-track': { bgcolor: '#4cc9f0' },
              '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          />
        </Box>

        <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'right', color: 'rgba(255,255,255,0.7)' }}>
          {availableYears[0]} - {availableYears[availableYears.length - 1]}
        </Typography>
      </Paper>
    </Box>
  );
}
